import { useGSAP } from '@gsap/react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { useRef } from 'react';
import { chipVideo, frameImg, frameVideo } from '../utils';
import { animateWithGsap } from '../utils/animations';
import { GlowingCard } from './ui/GlowingCard';
import { SparklesCore } from './ui/SparklesCore';

const HowItWorks = () => {
  const videoRef = useRef();
  const videoRef2 = useRef();

  useGSAP(() => {
    gsap.from('#chip', {
      scrollTrigger: {
        trigger: '#chip',
        start: '20% bottom'
      },
      opacity: 0,
      scale: 2,
      duration: 2,
      ease: 'power2.inOut'
    })

    animateWithGsap('.g_fadeIn', {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: 'power2.inOut'
    })
  }, []);

  const products = [
    { name: "Zero-Blockchain.XYZ", color: "from-cyan-400 to-teal-400" },
    { name: "Zero-Flow - Web 2.0", color: "from-white to-gray-300" },
    { name: "Zero-Crypto-Wallet - Web 3.0", color: "from-teal-400 to-cyan-400" },
    { name: "Zero-Pulse - Financial Assets", color: "from-gray-300 to-white" },
    { name: "Zero-Wave - Mobile Banking", color: "from-cyan-300 to-white" },
    { name: "Zero-Pay - Global Payments", color: "from-white to-cyan-300" },
    { name: "Zero-Comply - Reg. Compliance", color: "from-gray-400 to-gray-200" },
    { name: "Zero-Supervise - Market Monitoring", color: "from-teal-300 to-white" },
    { name: "Zero-Lend - P2P Lending", color: "from-white to-teal-300" },
    { name: "Zero-Core - Banking Solutions", color: "from-cyan-400 to-white" },
    { name: "Zero-Insure - Data-Driven Insurance", color: "from-gray-200 to-cyan-400" },
    { name: "Zero-Wealth - Investment Advisory", color: "from-cyan-300 to-gray-200" },
    { name: "Zero-Green - Eco Crowdfunding", color: "from-teal-400 to-gray-300" },
    { name: "Zero-Insight - AI Financial Advice", color: "from-white to-cyan-400" },
    { name: "Zero-Trade - AI Trading Desktop", color: "from-gray-300 to-teal-400" },
    { name: "Zero-Predict - Fraud Detection", color: "from-cyan-400 to-gray-300" },
    { name: "Zero-Stocks - AI Mobile Trading", color: "from-teal-300 to-gray-200" },
    { name: "Zero-Bank - Digital Money", color: "from-white to-teal-400" },
  ];

  return (
    <section className="common-padding relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-zinc-900/50 to-black" />
      
      <div className="screen-max-width relative z-10">
        <div id="chip" className="flex justify-center items-center w-full my-20 relative">
          {/* Sparkles around the chip video */}
          <div className="absolute inset-0 z-0">
            <SparklesCore
              id="chipSparkles"
              background="transparent"
              minSize={0.4}
              maxSize={1.5}
              particleDensity={50}
              particleColor="#06b6d4"
            />
          </div>
          <video 
            playsInline 
            id="chip-video" 
            className="w-1/2 h-auto object-cover object-center relative z-10" 
            preload="none" 
            muted 
            autoPlay 
            ref={videoRef}
          >
            <source src={chipVideo} type="video/mp4" />
          </video>
        </div>

        <div className="flex flex-col items-center">
          <motion.h2 
            className="hiw-title bg-gradient-to-r from-cyan-200 via-white to-cyan-200 bg-clip-text text-transparent"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Zero-Blockchain Technology.
            <br /> Transforming digital banking.
          </motion.h2>

          <motion.p 
            className="hiw-subtitle pb-0 text-gray-300"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Experience the revolution. Zero-Bank introduces the most advanced blockchain framework yet, redefining security, transparency, and speed in financial services.
          </motion.p>
        </div>

        <div className="mt-10 md:mt-20 mb-14">
          <motion.div 
            className="relative h-full flex-center"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <div className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-cyan-500/20">
              <img 
                src={frameImg}
                alt="frame"
                className="bg-transparent relative z-10"
              />
            </div>
            <div className="hiw-video">
              <video className="pointer-events-none" playsInline preload="none" muted autoPlay ref={videoRef2}>
                <source src={frameVideo} type="video/mp4" />
              </video>
            </div>
          </motion.div>
          <motion.p 
            className="text-gray-300 font-semibold text-center mt-9 pt-5 text-xl"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <span className="bg-gradient-to-r from-cyan-300 to-white bg-clip-text text-transparent">Zero-Bank:</span> Zero-Effort Investing
          </motion.p>
        </div>

        <div className="hiw-text-container gap-8">
          <GlowingCard 
            glowColor="cyan" 
            className="flex-1"
            icon={<span className="text-2xl">◈</span>}
            title="Security First"
          >
            <div className="flex flex-col gap-4">
              <p className="text-gray-300 leading-relaxed text-lg">
                Zero-Bank's platform leverages the cutting-edge Zero-Blockchain technology{' '}
                <span className="text-cyan-400 font-bold bg-cyan-400/10 px-1 rounded">
                  to deliver unprecedented security and efficiency in digital banking
                </span>.
              </p>

              <p className="text-gray-300 leading-relaxed text-lg">
                Experience the future of finance with seamless transactions,{' '}
                <span className="text-white font-bold bg-white/10 px-1 rounded">
                  unmatched speed, and the ultimate
                </span>{' '}
                in cryptographic security.
              </p>
            </div>
          </GlowingCard>

          <GlowingCard 
            glowColor="silver" 
            className="flex-1"
            icon={<span className="text-2xl">◎</span>}
            title="Our Ecosystem"
          >
            <div className="flex flex-col">
              <p className="text-gray-300 mb-4 text-sm uppercase tracking-wider">New on the Line</p>
              <div className="grid grid-cols-1 gap-1.5 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                {products.map((product, index) => (
                  <motion.p 
                    key={product.name}
                    className={`text-sm font-medium bg-gradient-to-r ${product.color} bg-clip-text text-transparent hover:scale-105 hover:pl-2 transition-all cursor-pointer py-1`}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.03 }}
                  >
                    {product.name}
                  </motion.p>
                ))}
              </div>
            </div>
          </GlowingCard>
        </div>
      </div>
    </section>
  )
}

export default HowItWorks